import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import blogService from '../../services/blogService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ArticleCard from './components/ArticleCard';
import TopicFilter from './components/TopicFilter';

const FinancialBlog = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [menuOpen, setMenuOpen] = useState(false);

  const isAdmin = userProfile?.email === 'demo@rovynbudget.com' || userProfile?.email === 'rovyngroupinc@gmail.com';

  useEffect(() => {
    loadArticles();
  }, [isAdmin]);

  useEffect(() => {
    applyFilters();
  }, [articles, selectedTopic, searchQuery, sortBy]);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const data = isAdmin
        ? await blogService?.getAllArticles(user?.id)
        : await blogService?.getPublishedArticles();
      setArticles(data);
    } catch (err) {
      setError(err?.message || 'Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...articles];

    // Topic filter
    if (selectedTopic !== 'all') {
      filtered = filtered?.filter(article => article?.topic === selectedTopic);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(
        article =>
          article?.title?.toLowerCase()?.includes(query) ||
          article?.excerpt?.toLowerCase()?.includes(query)
      );
    }

    // Sort
    if (sortBy === 'newest') {
      filtered?.sort((a, b) => new Date(b?.publishedAt || b?.createdAt) - new Date(a?.publishedAt || a?.createdAt));
    } else if (sortBy === 'oldest') {
      filtered?.sort((a, b) => new Date(a?.publishedAt || a?.createdAt) - new Date(b?.publishedAt || b?.createdAt));
    } else if (sortBy === 'popular') {
      filtered?.sort((a, b) => (b?.viewCount || 0) - (a?.viewCount || 0));
    }

    setFilteredArticles(filtered);
  };

  const handleCreateArticle = () => {
    navigate('/blog-article-editor');
  };

  const handleEditArticle = (articleId) => {
    navigate(`/blog-article-editor?id=${articleId}`);
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;

    try {
      await blogService?.deleteArticle(articleId);
      setArticles(articles?.filter(a => a?.id !== articleId));
    } catch (err) {
      alert(err?.message || 'Failed to delete article');
    }
  };

  const handleArticleClick = (slug) => {
    navigate(`/blog-article-view/${slug}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading articles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border-b border-border">
        <div className="max-w-7xl mx-auto px-16 sm:px-24 lg:px-32 py-32">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-16">
            <div className="flex items-center gap-16">
              {/* Hamburger Menu Icon */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="p-8 hover:bg-card rounded-lg transition-colors"
                aria-label="Toggle menu"
              >
                <Icon name="Menu" size={24} className="text-foreground" />
              </button>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-8">
                  Rovyn Financial Blog
                </h1>
                <p className="text-lg text-muted-foreground max-w-2xl mb-12">
                  Expert insights on budgeting, credit, debt management, and financial wellness
                </p>
                <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 max-w-2xl">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    <strong>Educational Disclaimer:</strong> This content is for educational purposes only and does not constitute financial advice. Always consider your personal situation or consult a qualified professional.
                  </p>
                </div>
              </div>
            </div>
            {isAdmin && (
              <Button
                onClick={handleCreateArticle}
                iconName="Plus"
                className="shrink-0"
              >
                Create Article
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {menuOpen && (
        <div className="bg-card border-b border-border shadow-lg">
          <div className="max-w-7xl mx-auto px-16 sm:px-24 lg:px-32 py-16">
            <nav className="flex flex-col gap-8">
              <button
                onClick={() => {
                  navigate('/dashboard');
                  setMenuOpen(false);
                }}
                className="flex items-center gap-12 px-16 py-12 hover:bg-muted rounded-lg transition-colors text-left"
              >
                <Icon name="Home" size={20} />
                <span className="text-foreground">Dashboard</span>
              </button>
              <button
                onClick={() => {
                  navigate('/financial-blog');
                  setMenuOpen(false);
                }}
                className="flex items-center gap-12 px-16 py-12 bg-muted rounded-lg text-left"
              >
                <Icon name="BookOpen" size={20} />
                <span className="text-foreground font-medium">Blog</span>
              </button>
              {isAdmin && (
                <button
                  onClick={() => {
                    handleCreateArticle();
                    setMenuOpen(false);
                  }}
                  className="flex items-center gap-12 px-16 py-12 hover:bg-muted rounded-lg transition-colors text-left"
                >
                  <Icon name="Plus" size={20} />
                  <span className="text-foreground">Create Article</span>
                </button>
              )}
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-16 sm:px-24 lg:px-32 py-32">
        {/* Filters */}
        <div className="mb-32">
          <div className="flex flex-col lg:flex-row gap-16 mb-24">
            {/* Topic Filter */}
            <TopicFilter
              selectedTopic={selectedTopic}
              onTopicChange={setSelectedTopic}
            />

            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Icon
                  name="Search"
                  size={20}
                  className="absolute left-12 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full pl-44 pr-16 py-12 bg-card border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* Sort */}
            <div className="w-full lg:w-auto">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e?.target?.value)}
                className="w-full px-16 py-12 bg-card border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="popular">Most Popular</option>
              </select>
            </div>
          </div>

          {/* Results count */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {filteredArticles?.length} {filteredArticles?.length === 1 ? 'article' : 'articles'} found
            </p>
            {(selectedTopic !== 'all' || searchQuery) && (
              <button
                onClick={() => {
                  setSelectedTopic('all');
                  setSearchQuery('');
                }}
                className="text-sm text-primary hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-24 p-16 bg-destructive/10 border border-destructive rounded-lg">
            <p className="text-destructive">{error}</p>
          </div>
        )}

        {/* Articles Grid */}
        {filteredArticles?.length === 0 ? (
          <div className="text-center py-64">
            <Icon name="FileText" size={64} className="mx-auto mb-16 text-muted-foreground" />
            <h3 className="text-xl font-semibold text-foreground mb-8">No articles found</h3>
            <p className="text-muted-foreground mb-24">
              {searchQuery || selectedTopic !== 'all' ?'Try adjusting your filters' :'Check back soon for new content'}
            </p>
            {isAdmin && (
              <Button onClick={handleCreateArticle} iconName="Plus">
                Create First Article
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-24">
            {filteredArticles?.map((article) => (
              <ArticleCard
                key={article?.id}
                article={article}
                isAdmin={isAdmin}
                onEdit={() => handleEditArticle(article?.id)}
                onDelete={() => handleDeleteArticle(article?.id)}
                onClick={() => handleArticleClick(article?.slug)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer Disclaimer */}
      <div className="bg-muted border-t border-border py-16">
        <div className="max-w-7xl mx-auto px-16 sm:px-24 lg:px-32 text-center">
          <p className="text-sm text-muted-foreground italic">
            Rovyn Budget does not provide individualized financial advice.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FinancialBlog;